import { diffInMs } from '~/utils/date';

import type { HistoryEvent } from '../types/workflow-inspection.types';

export type SpanKind = 'workflow' | 'activity' | 'timer' | 'childWorkflow' | 'marker' | 'signal';
export type SpanStatus = 'running' | 'completed' | 'failed' | 'canceled' | 'timedOut';

/**
 * One lane on the event-history timeline / one row of the Compact view: an
 * activity (Scheduled → Started → closed), timer, child workflow, or a point
 * event (marker, signal). Times are ms offsets from the workflow's first event.
 */
export interface HistorySpan {
  /** Stable key — the opening event's id. */
  key: number;
  kind: SpanKind;
  /** Activity type / timer id / child workflow type / marker name. */
  label: string;
  status: SpanStatus;
  startMs: number;
  /** `null` while still open. */
  endMs: number | null;
  /** Every event that belongs to this span, ascending. */
  eventIds: number[];
  /** Highest attempt seen on a Started event (activities). */
  attempts: number | null;
  /** Failure message from the closing event, when it failed. */
  failureMessage: string | null;
}

export interface HistoryTimeline {
  /** Workflow-level span (first event → close event). */
  workflow: HistorySpan | null;
  /** Child spans ordered by start time. */
  spans: HistorySpan[];
  /** Total ms covered (to the close event, or the latest event while running). */
  totalMs: number;
}

const ACTIVITY_CLOSE: Record<string, SpanStatus> = {
  ActivityTaskCompleted: 'completed',
  ActivityTaskFailed: 'failed',
  ActivityTaskTimedOut: 'timedOut',
  ActivityTaskCanceled: 'canceled',
};
const CHILD_CLOSE: Record<string, SpanStatus> = {
  ChildWorkflowExecutionCompleted: 'completed',
  ChildWorkflowExecutionFailed: 'failed',
  ChildWorkflowExecutionTimedOut: 'timedOut',
  ChildWorkflowExecutionCanceled: 'canceled',
  ChildWorkflowExecutionTerminated: 'canceled',
  StartChildWorkflowExecutionFailed: 'failed',
};
const WORKFLOW_CLOSE: Record<string, SpanStatus> = {
  WorkflowExecutionCompleted: 'completed',
  WorkflowExecutionFailed: 'failed',
  WorkflowExecutionTimedOut: 'timedOut',
  WorkflowExecutionCanceled: 'canceled',
  WorkflowExecutionTerminated: 'canceled',
  WorkflowExecutionContinuedAsNew: 'completed',
};

/** Read a numeric attribute (proto-JSON int64s can arrive as strings). */
function numAttr(event: HistoryEvent, key: string): number | null {
  const raw = event.attributes[key];
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw)))
    return Number(raw);
  return null;
}

/** Read a nested `{ name }` attribute, e.g. `workflowType.name`. */
function nameAttr(event: HistoryEvent, key: string): string | null {
  const raw = event.attributes[key];
  if (typeof raw === 'string') return raw;
  if (
    typeof raw === 'object' &&
    raw !== null &&
    typeof (raw as { name?: unknown }).name === 'string'
  )
    return (raw as { name: string }).name;
  return null;
}

/**
 * Fold an ascending event list into timeline spans: one per activity (joined on
 * `scheduledEventId`), timer (`startedEventId`), child workflow
 * (`initiatedEventId`), plus point spans for markers and signals. Workflow-task
 * events are bookkeeping and get no lane (they still show in the table).
 */
export function buildHistoryTimeline(events: HistoryEvent[]): HistoryTimeline {
  const first = events[0];
  if (!first) return { workflow: null, spans: [], totalMs: 0 };
  const origin = first.eventTime;
  const offset = (e: HistoryEvent) => Math.max(0, diffInMs(origin, e.eventTime));
  const last = events[events.length - 1] ?? first;

  const open = new Map<number, HistorySpan>();
  const spans: HistorySpan[] = [];
  let workflow: HistorySpan = {
    key: first.eventId,
    kind: 'workflow',
    label: nameAttr(first, 'workflowType') ?? 'Workflow',
    status: 'running',
    startMs: 0,
    endMs: null,
    eventIds: [first.eventId],
    attempts: null,
    failureMessage: null,
  };

  const openSpan = (e: HistoryEvent, kind: SpanKind, label: string) => {
    const span: HistorySpan = {
      key: e.eventId,
      kind,
      label,
      status: 'running',
      startMs: offset(e),
      endMs: null,
      eventIds: [e.eventId],
      attempts: null,
      failureMessage: null,
    };
    open.set(e.eventId, span);
    spans.push(span);
  };
  const closeSpan = (refId: number | null, e: HistoryEvent, status: SpanStatus | null) => {
    const span = refId === null ? undefined : open.get(refId);
    if (!span) return;
    span.eventIds.push(e.eventId);
    if (status) {
      span.status = status;
      span.endMs = offset(e);
      span.failureMessage = e.failure?.message ?? e.failure?.cause?.message ?? null;
    }
  };
  const pointSpan = (e: HistoryEvent, kind: SpanKind, label: string) => {
    const at = offset(e);
    spans.push({
      key: e.eventId,
      kind,
      label,
      status: 'completed',
      startMs: at,
      endMs: at,
      eventIds: [e.eventId],
      attempts: null,
      failureMessage: null,
    });
  };

  for (const e of events) {
    const type = e.eventType;
    if (type === 'ActivityTaskScheduled') {
      openSpan(
        e,
        'activity',
        e.activityType ?? nameAttr(e, 'activityType') ?? `Activity ${e.activityId ?? ''}`,
      );
    } else if (type === 'ActivityTaskStarted') {
      closeSpan(e.scheduledEventId, e, null);
      const span = e.scheduledEventId === null ? undefined : open.get(e.scheduledEventId);
      if (span && e.attempt !== null) span.attempts = Math.max(span.attempts ?? 0, e.attempt);
    } else if (type in ACTIVITY_CLOSE) {
      closeSpan(e.scheduledEventId, e, ACTIVITY_CLOSE[type] ?? null);
    } else if (type === 'TimerStarted') {
      openSpan(
        e,
        'timer',
        `Timer ${typeof e.attributes.timerId === 'string' ? e.attributes.timerId : ''}`.trim(),
      );
    } else if (type === 'TimerFired' || type === 'TimerCanceled') {
      closeSpan(numAttr(e, 'startedEventId'), e, type === 'TimerFired' ? 'completed' : 'canceled');
    } else if (type === 'StartChildWorkflowExecutionInitiated') {
      openSpan(e, 'childWorkflow', nameAttr(e, 'workflowType') ?? 'Child workflow');
    } else if (type === 'ChildWorkflowExecutionStarted') {
      closeSpan(numAttr(e, 'initiatedEventId'), e, null);
    } else if (type in CHILD_CLOSE) {
      closeSpan(numAttr(e, 'initiatedEventId'), e, CHILD_CLOSE[type] ?? null);
    } else if (type === 'MarkerRecorded') {
      pointSpan(e, 'marker', e.markerName ?? 'Marker');
    } else if (type === 'WorkflowExecutionSignaled') {
      pointSpan(
        e,
        'signal',
        typeof e.attributes.signalName === 'string' ? e.attributes.signalName : 'Signal',
      );
    } else if (type in WORKFLOW_CLOSE) {
      workflow = {
        ...workflow,
        status: WORKFLOW_CLOSE[type] ?? 'completed',
        endMs: offset(e),
        failureMessage: e.failure?.message ?? null,
      };
    }
  }

  const totalMs = workflow.endMs ?? offset(last);
  spans.sort((a, b) => a.startMs - b.startMs || a.key - b.key);
  return { workflow, spans, totalMs };
}

/** `ActivityTaskScheduled` → `Activity Task Scheduled` for display. */
export function humanizeEventType(eventType: string): string {
  return eventType.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
}
